import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import i18n, { SUPPORTED } from "@/i18n";
import Home from "@/platform/Home";
import Annonces from "@/platform/Annonces";
import PropertyDetail from "@/platform/PropertyDetail";
import OfferForm from "@/platform/OfferForm";
import SellerSpace from "@/platform/SellerSpace";
import BuyerOffer from "@/platform/BuyerOffer";
import Agence from "@/platform/Agence";
import Services from "@/platform/Services";
import ProposerBien from "@/platform/ProposerBien";
import ProprietaireSpace from "@/platform/ProprietaireSpace";
import Private from "@/platform/private/Private";
import MemberLogin from "@/platform/MemberLogin";
import MemberRegister from "@/platform/MemberRegister";
import JoinNetwork from "@/platform/JoinNetwork";
import MemberDashboard from "@/platform/MemberDashboard";
import VisiteIA from "@/platform/VisiteIA";
import SellerPortal from "@/platform/SellerPortal";
import AcquereurPortal from "@/platform/AcquereurPortal";
import MemberPage from "@/platform/MemberPage";
import PartnerSite from "@/platform/PartnerSite";
import Partners from "@/platform/Partners";
import Favoris from "@/platform/Favoris";
import { AuthProvider } from "@/platform/authApi";
import Login from "@/platform/admin/Login";
import Dashboard from "@/platform/admin/Dashboard";
import ListingForm from "@/platform/admin/ListingForm";
import NewListingChoice from "@/platform/admin/NewListingChoice";
import Submissions from "@/platform/admin/Submissions";
import SubmissionEdit from "@/platform/admin/SubmissionEdit";
import AuthCallback from "@/platform/admin/AuthCallback";
import ProtectedRoute from "@/platform/admin/ProtectedRoute";
import Affiliations from "@/platform/admin/Affiliations";
import Stats from "@/platform/admin/Stats";
import AdminCredits from "@/platform/admin/AdminCredits";
import AdminOffers from "@/platform/admin/AdminOffers";
import AdminROI from "@/platform/admin/AdminROI";
import AdminFinance from "@/platform/admin/AdminFinance";
import AcquereurRegister from "@/platform/AcquereurRegister";
import AdminAcquereurs from "@/platform/admin/AdminAcquereurs";
import AdminSupervision from "@/platform/admin/AdminSupervision";
import AdminAgent from "@/platform/admin/AdminAgent";import { PaymentSuccess, PaymentCancel } from "@/platform/PaymentResult";
import InstallApp from "@/platform/InstallApp";
import GeoLangBanner from "@/platform/GeoLangBanner";
import OffMarketDefinition from "@/platform/OffMarketDefinition";
import FilmCardCapture from "@/platform/FilmCardCapture";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [pathname]);
  return null;
}

// Gère les URLs préfixées par langue (/fr /en /es /de /pt /zh-cn) : fixe la langue puis
// redirige vers le chemin « propre » (non-destructif — les URLs existantes ne changent pas).
const LANG_URL = { fr: "fr", en: "en", es: "es", de: "de", pt: "pt", "zh-cn": "zh-CN" };
function LangGate() {
  const { pathname, search, hash } = useLocation();
  const seg = (pathname.split("/")[1] || "").toLowerCase();
  const mapped = LANG_URL[seg];
  if (mapped) {
    const rest = pathname.slice(seg.length + 1) || "/";
    if (i18n.language !== mapped) { try { i18n.changeLanguage(mapped); } catch (_) {} }
    return <Navigate to={`${rest}${search}${hash}`} replace />;
  }
  return <AppRoutes />;
}

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth callback (session_id in URL fragment) BEFORE any route/guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/biens-off-market" element={<Annonces />} />
      <Route path="/annonces" element={<Navigate to="/biens-off-market" replace />} />
      <Route path="/philosophie" element={<Agence />} />
      <Route path="/agence" element={<Navigate to="/philosophie" replace />} />
      <Route path="/expertises" element={<Services />} />
      <Route path="/services" element={<Navigate to="/expertises" replace />} />
      <Route path="/immobilier-off-market" element={<OffMarketDefinition />} />
      <Route path="/proposer" element={<ProposerBien />} />
      <Route path="/espace-proprietaire" element={<ProprietaireSpace />} />
      <Route path="/private" element={<Private />} />
      <Route path="/nos-conseillers-et-agents-immobilier" element={<Partners />} />
      <Route path="/favoris" element={<Favoris />} />
      <Route path="/connexion" element={<MemberLogin />} />
      <Route path="/inscription" element={<MemberRegister />} />
      <Route path="/rejoindre" element={<JoinNetwork />} />
      <Route path="/mon-compte" element={<MemberDashboard />} />
      <Route path="/mon-compte/:tab" element={<MemberDashboard />} />
      <Route path="/visite-ia" element={<VisiteIA />} />
      <Route path="/vendeur-espace/:token" element={<SellerPortal />} />
      <Route path="/acquereur-espace/:token" element={<AcquereurPortal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/bien/:slug" element={<PropertyDetail />} />
      <Route path="/bien/:slug/offre" element={<OfferForm />} />
      <Route path="/vendeur/:token" element={<SellerSpace />} />
      <Route path="/offre/:id" element={<BuyerOffer />} />
      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/affiliations" element={<ProtectedRoute><Affiliations /></ProtectedRoute>} />
      <Route path="/admin/supervision" element={<ProtectedRoute><AdminSupervision /></ProtectedRoute>} />
      <Route path="/admin/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
      <Route path="/admin/roi" element={<ProtectedRoute><AdminROI /></ProtectedRoute>} />
      <Route path="/admin/finance" element={<ProtectedRoute><AdminFinance /></ProtectedRoute>} />
      <Route path="/admin/credits" element={<ProtectedRoute><AdminCredits /></ProtectedRoute>} />
      <Route path="/admin/agent" element={<ProtectedRoute><AdminAgent /></ProtectedRoute>} />
      <Route path="/admin/acquereurs" element={<ProtectedRoute><AdminAcquereurs /></ProtectedRoute>} />
      <Route path="/admin/offers" element={<ProtectedRoute><AdminOffers /></ProtectedRoute>} />
      <Route path="/espace-acquereurs" element={<AcquereurRegister />} />
      <Route path="/admin/new" element={<ProtectedRoute><NewListingChoice /></ProtectedRoute>} />
      <Route path="/admin/new/manual" element={<ProtectedRoute><ListingForm /></ProtectedRoute>} />
      <Route path="/admin/edit/:slug" element={<ProtectedRoute><ListingForm /></ProtectedRoute>} />
      <Route path="/admin/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
      <Route path="/admin/submissions/:id/edit" element={<ProtectedRoute><SubmissionEdit /></ProtectedRoute>} />
      <Route path="/filmcard" element={<FilmCardCapture />} />
      <Route path="/:slug/site/news/:article" element={<PartnerSite />} />
      <Route path="/:slug/site" element={<PartnerSite />} />
      <Route path="/:slug/site/:page" element={<PartnerSite />} />
      <Route path="/:slug" element={<MemberPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const rootEl = document.getElementById("root");
const appTree = (
  <React.StrictMode>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <LangGate />
          <InstallApp />
          <GeoLangBanner />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);
// Hydrate le HTML pré-rendu (react-snap) en production ; sinon rendu client classique.
if (rootEl.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootEl, appTree);
} else {
  ReactDOM.createRoot(rootEl).render(appTree);
}

// Enregistrement du service worker (PWA installable)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
